// ======================================================================
// FILE:
// src/modules/finance/transactions/contra_voucher/pages/ContraVoucherListPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// PAGE:
// Contra Voucher List
//
// PURPOSE:
// Displays Finance Contra Voucher transactions.
//
// FEATURES:
// - KPI Summary
// - Search & Filters
// - Contra Voucher Grid
// - Workflow Actions
// - New Contra Voucher
// - Responsive layout
//
// IMPORTANT:
// - Uses existing Finance shared components
// - Uses existing financeLookupService
// - Uses contraVoucherService
// - No Redux
// - No accounting logic
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";


// ======================================================================
// FINANCE COMMON COMPONENTS
// ======================================================================

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import FinanceStatusBadge
    from "../../../components/FinanceStatusBadge";

import FinanceActionButtons
    from "../../../components/FinanceActionButtons";


// ======================================================================
// SERVICES
// ======================================================================

import contraVoucherService
    from "../services/contraVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function ContraVoucherListPage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // LOADING
    // ==================================================================

    const [
        loading,
        setLoading
    ] = useState(false);


    // ==================================================================
    // DATA
    // ==================================================================

    const [
        vouchers,
        setVouchers
    ] = useState([]);


    // ==================================================================
    // ERROR
    // ==================================================================

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // COMPANY
    // ==================================================================

    const [
        selectedCompany,
        setSelectedCompany
    ] = useState(null);


    // ==================================================================
    // FILTERS
    // ==================================================================

    const [
        filters,
        setFilters
    ] = useState({

        company_id:
            null,

        search:
            "",

        status:
            "",

        date_from:
            "",

        date_to:
            "",

    });


    // ==================================================================
    // STATUS OPTIONS
    // ==================================================================

    const statusOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "DRAFT",
                    label: "Draft",
                },

                {
                    value: "SUBMITTED",
                    label: "Submitted",
                },

                {
                    value: "APPROVED",
                    label: "Approved",
                },

                {
                    value: "POSTED",
                    label: "Posted",
                },

                {
                    value: "CANCELLED",
                    label: "Cancelled",
                },

            ],
            []
        );


    // ==================================================================
    // LOAD ON COMPANY CHANGE
    // ==================================================================

    useEffect(
        () => {

            /*
             * Contra Voucher list is company scoped.
             *
             * Therefore do not call the API until
             * a company has been selected.
             */

            if (
                !filters.company_id
            ) {

                setVouchers([]);

                return;

            }


            loadContraVouchers(
                filters
            );

        },
        [
            filters.company_id
        ]
    );


    // ==================================================================
    // LOAD CONTRA VOUCHERS
    // ==================================================================

    async function loadContraVouchers(
        appliedFilters = filters
    ) {

        if (
            !appliedFilters.company_id
        ) {

            setVouchers([]);

            return;

        }


        try {

            setLoading(true);

            setErrorMessage("");


            const result =
                await contraVoucherService
                    .listContraVouchers(

                        {

                            company_id:
                                appliedFilters.company_id,

                            search:
                                appliedFilters.search ||
                                undefined,

                            status:
                                appliedFilters.status ||
                                undefined,

                            date_from:
                                appliedFilters.date_from ||
                                undefined,

                            date_to:
                                appliedFilters.date_to ||
                                undefined,

                        }

                    );


            setVouchers(

                Array.isArray(
                    result?.rows
                )
                    ? result.rows
                    : []

            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to load Contra Vouchers:",
                error
            );


            setVouchers([]);


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to load Contra Vouchers."

            );

        }
        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // UPDATE FILTER
    // ==================================================================

    function updateFilter(
        field,
        value
    ) {

        setFilters(
            previous => ({

                ...previous,

                [field]:
                    value,

            })
        );

    }


    // ==================================================================
    // SEARCH
    // ==================================================================

    function handleSearch() {

        if (
            !filters.company_id
        ) {

            setErrorMessage(
                "Please select a Company."
            );

            return;

        }


        loadContraVouchers(
            filters
        );

    }


    // ==================================================================
    // RESET
    // ==================================================================

    function handleReset() {

        const resetFilters = {

            company_id:
                selectedCompany?.value ||
                null,

            search:
                "",

            status:
                "",

            date_from:
                "",

            date_to:
                "",

        };


        setFilters(
            resetFilters
        );


        setErrorMessage("");


        if (
            resetFilters.company_id
        ) {

            loadContraVouchers(
                resetFilters
            );

        }
        else {

            setVouchers([]);

        }

    }


    // ==================================================================
    // NAVIGATION - CREATE
    // ==================================================================

    function handleCreate() {

        navigate(
            "/finance/contra-vouchers/create"
        );

    }


    // ==================================================================
    // NAVIGATION - VIEW
    // ==================================================================

    function handleView(
        row
    ) {

        navigate(
            `/finance/contra-vouchers/view/${row.id}`
        );

    }


    // ==================================================================
    // NAVIGATION - EDIT
    // ==================================================================

    function handleEdit(
        row
    ) {

        navigate(
            `/finance/contra-vouchers/edit/${row.id}`
        );

    }


    // ==================================================================
    // DELETE
    // ==================================================================

    async function handleDelete(
        row
    ) {

        if (
            !window.confirm(
                "Delete selected Contra Voucher?"
            )
        ) {

            return;

        }


        try {

            setErrorMessage("");


            await contraVoucherService
                .deleteContraVoucher(

                    row.id,

                    filters.company_id

                );


            await loadContraVouchers(
                filters
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to delete Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to delete Contra Voucher."

            );

        }

    }


    // ==================================================================
    // SUBMIT
    // ==================================================================

    async function handleSubmit(
        row
    ) {

        try {

            setErrorMessage("");


            await contraVoucherService
                .submitContraVoucher(

                    row.id,

                    filters.company_id

                );


            await loadContraVouchers(
                filters
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to submit Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to submit Contra Voucher."

            );

        }

    }


    // ==================================================================
    // APPROVE
    // ==================================================================

    async function handleApprove(
        row
    ) {

        try {

            setErrorMessage("");


            await contraVoucherService
                .approveContraVoucher(

                    row.id,

                    filters.company_id

                );


            await loadContraVouchers(
                filters
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to approve Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to approve Contra Voucher."

            );

        }

    }


    // ==================================================================
    // POST
    // ==================================================================

    async function handlePost(
        row
    ) {

        try {

            setErrorMessage("");


            await contraVoucherService
                .postContraVoucher(

                    row.id,

                    filters.company_id

                );


            await loadContraVouchers(
                filters
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to post Contra Voucher:",
                error
            );


            setErrorMessage(

                error?.response?.data?.message ||

                error?.response?.data?.error ||

                error?.message ||

                "Failed to post Contra Voucher."

            );

        }

    }


    // ==================================================================
    // ASYNC COMPANY LOADER
    // ==================================================================

    async function loadCompanies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                })
            );

        }
        catch (
            error
        ) {

            console.error(
                "Company lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // SUMMARY
    // ==================================================================

    const summary =
        useMemo(
            () => {

                const result = {

                    total:
                        vouchers.length,

                    draft:
                        0,

                    submitted:
                        0,

                    approved:
                        0,

                    posted:
                        0,

                    cancelled:
                        0,

                };


                vouchers.forEach(
                    voucher => {

                        const status =
                            String(
                                voucher.status ||
                                ""
                            ).toUpperCase();


                        if (
                            status ===
                            "DRAFT"
                        ) {

                            result.draft += 1;

                        }
                        else if (
                            status ===
                            "SUBMITTED"
                        ) {

                            result.submitted += 1;

                        }
                        else if (
                            status ===
                            "APPROVED"
                        ) {

                            result.approved += 1;

                        }
                        else if (
                            status ===
                            "POSTED"
                        ) {

                            result.posted += 1;

                        }
                        else if (
                            status ===
                            "CANCELLED"
                        ) {

                            result.cancelled += 1;

                        }

                    }
                );


                return result;

            },
            [
                vouchers
            ]
        );


    // ==================================================================
    // SUMMARY CARDS
    // ==================================================================

    const summaryCards = [

        {

            key:
                "total",

            title:
                "Total Contra Vouchers",

            value:
                summary.total,

            icon:
                "fas fa-exchange-alt",

            background:
                "#0d6efd",

        },

        {

            key:
                "draft",

            title:
                "Draft",

            value:
                summary.draft,

            icon:
                "fas fa-pencil-alt",

            background:
                "#6c757d",

        },

        {

            key:
                "submitted",

            title:
                "Submitted",

            value:
                summary.submitted,

            icon:
                "fas fa-paper-plane",

            background:
                "#ffc107",

        },

        {

            key:
                "approved",

            title:
                "Approved",

            value:
                summary.approved,

            icon:
                "fas fa-check",

            background:
                "#198754",

        },

        {

            key:
                "posted",

            title:
                "Posted",

            value:
                summary.posted,

            icon:
                "fas fa-check-double",

            background:
                "#0dcaf0",

        },

    ];


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-exchange-alt"

                title="Contra Voucher"

                subtitle={
                    "Create and manage Finance Contra Vouchers."
                }

                breadcrumbs={[

                    {

                        label:
                            "Finance",

                        to:
                            "/finance",

                    },

                    {

                        label:
                            "Contra Voucher",

                    },

                ]}

                actions={

                    <button

                        type="button"

                        className="
                            btn
                            btn-primary
                        "

                        onClick={
                            handleCreate
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-1
                            "
                        />

                        New Contra Voucher

                    </button>

                }

            />


            {/* ==========================================================
                SUMMARY
            ========================================================== */}

            <FinanceSummaryCards

                cards={
                    summaryCards
                }

            />


            {/* ==========================================================
                SEARCH / FILTERS
            ========================================================== */}

            <FinanceSearchPanel

                loading={
                    loading
                }

                onSearch={
                    handleSearch
                }

                onReset={
                    handleReset
                }

            >

                {/* ======================================================
                    COMPANY
                ====================================================== */}

                <div
                    className="
                        col-lg-3
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >

                        Company

                    </label>


                    <AsyncSelect

                        cacheOptions

                        defaultOptions

                        loadOptions={
                            loadCompanies
                        }

                        isClearable

                        value={
                            selectedCompany
                        }

                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value ||
                                    null
                                );

                            }
                        }

                        placeholder="Select Company..."

                        menuPortalTarget={
                            document.body
                        }

                        styles={{
                            menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}

                    />

                </div>


                {/* ======================================================
                    SEARCH
                ====================================================== */}

                <div
                    className="
                        col-lg-3
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >

                        Search

                    </label>


                    <input

                        className="form-control"

                        value={
                            filters.search
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "search",
                                    event.target.value
                                )
                        }

                        placeholder="
                            Voucher No / Account / Reference
                        "

                    />

                </div>


                {/* ======================================================
                    STATUS
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >

                        Status

                    </label>


                    <Select

                        options={
                            statusOptions
                        }

                        value={
                            statusOptions.find(
                                option =>
                                    option.value ===
                                    filters.status
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "status",
                                    option?.value ||
                                    ""
                                )
                        }

                        menuPortalTarget={
                            document.body
                        }

                        styles={{
                            menuPortal: base => ({
                                ...base,
                                zIndex: 9999,
                            }),
                        }}

                    />

                </div>


                {/* ======================================================
                    DATE FROM
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >

                        Date From

                    </label>


                    <input

                        type="date"

                        className="form-control"

                        value={
                            filters.date_from
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "date_from",
                                    event.target.value
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    DATE TO
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >

                        Date To

                    </label>


                    <input

                        type="date"

                        className="form-control"

                        value={
                            filters.date_to
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "date_to",
                                    event.target.value
                                )
                        }

                    />

                </div>

            </FinanceSearchPanel>


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                CONTRA VOUCHER GRID
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                "
            >

                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-hover
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 60,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        Voucher No
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        From Account
                                    </th>

                                    <th>
                                        To Account
                                    </th>

                                    <th
                                        className="
                                            text-end
                                        "
                                    >
                                        Amount
                                    </th>

                                    <th>
                                        Currency
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Approval
                                    </th>

                                    <th
                                        className="
                                            text-center
                                        "
                                        style={{
                                            width: 220,
                                        }}
                                    >
                                        Actions
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan={10}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        spinner-border
                                                        text-primary
                                                    "
                                                    role="status"
                                                />

                                            </td>

                                        </tr>

                                    ) : !filters.company_id ? (

                                        <tr>

                                            <td
                                                colSpan={10}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-building
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>
                                                        Select Company
                                                    </h5>

                                                    <p>
                                                        Select a Company
                                                        to view Contra
                                                        Vouchers.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : vouchers.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan={10}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-exchange-alt
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>
                                                        No Contra
                                                        Vouchers Found
                                                    </h5>

                                                    <p>
                                                        Create your first
                                                        Contra Voucher.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : (

                                        vouchers.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        row.id
                                                    }
                                                >

                                                    {/* ==================================
                                                        #
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            index + 1
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        VOUCHER NO
                                                    ================================== */}

                                                    <td>

                                                        <button

                                                            type="button"

                                                            className="
                                                                btn
                                                                btn-link
                                                                p-0
                                                            "

                                                            onClick={() =>
                                                                handleView(
                                                                    row
                                                                )
                                                            }

                                                        >

                                                            {
                                                                row.voucher_no ||
                                                                "-"
                                                            }

                                                        </button>

                                                    </td>


                                                    {/* ==================================
                                                        DATE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.voucher_date
                                                                ? String(
                                                                    row.voucher_date
                                                                ).slice(
                                                                    0,
                                                                    10
                                                                )
                                                                : "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        FROM ACCOUNT
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.from_account_name ||

                                                            row.from_account ||

                                                            row.from_account_id ||

                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        TO ACCOUNT
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.to_account_name ||

                                                            row.to_account ||

                                                            row.to_account_id ||

                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        AMOUNT
                                                    ================================== */}

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >

                                                        {
                                                            Number(
                                                                row.transaction_amount ||
                                                                0
                                                            ).toLocaleString(
                                                                "en-IN",
                                                                {

                                                                    minimumFractionDigits:
                                                                        2,

                                                                    maximumFractionDigits:
                                                                        2,

                                                                }
                                                            )
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        CURRENCY
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.currency_code ||

                                                            row.currency_name ||

                                                            row.currency_id ||

                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        STATUS
                                                    ================================== */}

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="journal"

                                                            value={
                                                                row.status
                                                            }

                                                        />

                                                    </td>


                                                    {/* ==================================
                                                        APPROVAL
                                                    ================================== */}

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="approval"

                                                            value={
                                                                row.approval_status
                                                            }

                                                        />

                                                    </td>


                                                    {/* ==================================
                                                        ACTIONS
                                                    ================================== */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <FinanceActionButtons

                                                            row={
                                                                row
                                                            }

                                                            onView={
                                                                handleView
                                                            }

                                                            onEdit={
                                                                handleEdit
                                                            }

                                                            onDelete={
                                                                handleDelete
                                                            }

                                                            onSubmit={
                                                                handleSubmit
                                                            }

                                                            onApprove={
                                                                handleApprove
                                                            }

                                                            onPost={
                                                                handlePost
                                                            }

                                                        />

                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                RESULT COUNT
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mt-4
                "
            >

                <small
                    className="text-muted"
                >

                    Total Records:

                    <strong
                        className="ms-1"
                    >

                        {
                            vouchers.length
                        }

                    </strong>

                </small>

            </div>

        </>

    );

}